/** Default public Privacy Policy body (markdown). Used when studio settings have no override. */
export const PRIVACY_POLICY_EFFECTIVE = "08/04/2026";
export const PRIVACY_POLICY_UPDATED = "08/04/2026";

export const DEFAULT_PRIVACY_POLICY = `# Privacy Policy — TAĪSTU

**Effective date: ${PRIVACY_POLICY_EFFECTIVE}**
**Last updated: ${PRIVACY_POLICY_UPDATED}**

TAĪSTU ("TAĪSTU," "we," "us," or "our") operates its platform — TAĪSTU's operating system — at **one.taistu.com**, the software we use to run our design and systems studio: shaping scopes, managing engagements, sending documents, and keeping our client work organized. This Privacy Policy explains what information we handle through the platform, how we use and protect it, who we share it with, and the choices you have.

We build client-owned infrastructure for a living, and we treat the information inside our platform with the same care we bring to the systems we build.

---

## 1. Who and what this policy covers

This policy applies to the TAĪSTU platform at one.taistu.com and to the people whose information we process through it, including:

- Studio clients and the businesses we work with;
- Prospects who review a proposal or scope through a shared link;
- Invited client users who access a project workspace; and
- Business contacts we track (clients, suppliers, and partners).

**A note on the systems we build for clients.** When TAĪSTU builds a custom system or portal and hands it off to a client, that client operates it under their own terms and is responsible for the information in it. This policy covers only the TAĪSTU platform that we operate at one.taistu.com.

---

## 2. Information we collect

**Account information.** For administrators and invited client users: name, email address, a password (stored only as a salted, hashed value — never in plain text), a profile image (typically your company logo), and two-factor authentication settings.

**Contact and relationship information.** For clients, prospects, suppliers, and partners: names, business and company details, contact information, relationship status, and notes we keep to manage the working relationship.

**Engagement and project information.** Project details, timelines, files uploaded to a workspace library, scope and proposal documents, and invoices and their payment status. Invoicing is tracked internally for our records — **we do not process or store payment card numbers in the platform.**

**Meeting and calendar information.** Meeting times and notes, including information synced from Google Calendar and notes maintained in Notion, when those integrations are connected.

**Scope- and proposal-review responses.** If you review a scope or proposal through a shared link, we record your acceptance or change requests and any notes you submit.

**Communications.** Transactional messages we send — such as sign-in links, proposal and invoice packets, and reminders — delivered through our email provider.

**Technical and security information.** An essential session cookie to keep you signed in, and security event logs (such as sign-in, sign-out, two-factor, and password-change events) used to protect accounts and detect abuse.

We do **not** use advertising cookies, analytics trackers, or cross-site tracking of any kind.

---

## 3. How we collect information

- **Directly from you** — when you're invited to a workspace, review a scope, or communicate with us.
- **Through our work together** — information you share so we can deliver an engagement.
- **Automatically** — the session cookie and security logs described above.
- **Through connected integrations** — Google Calendar and Notion, only when you or we authorize the connection.

---

## 4. How we use information

We use the information we collect to:

- Provide and operate the TAĪSTU platform and your project workspace;
- Manage engagements, scopes, proposals, files, and invoicing;
- Communicate with you about your project;
- Draft scopes, proposals, invoices, and notes using our internal AI assistant (see Section 5);
- Keep the platform secure, prevent abuse, and maintain audit records; and
- Meet our legal, accounting, and record-keeping obligations.

---

## 5. AI assistance (BLU)

Our internal AI assistant, **BLU**, helps studio administrators draft scopes, proposals, invoices, and notes. To generate a draft, BLU processes relevant engagement content (such as scope context or meeting notes) through our AI provider, Anthropic. BLU is available to studio administrators only, and AI-generated drafts are reviewed by a person before anything is sent. **Your information is not used to train third-party AI models.**

---

## 6. How we share information

**We do not sell your information.** We share it only with the service providers that help us operate the TAĪSTU platform, and only as needed for them to perform their service. These providers include:

- **MongoDB Atlas** — Database hosting
- **Railway** — Application and server hosting
- **Vercel** — Front-end hosting
- **Resend** — Transactional email delivery
- **Google** — Sign-in and Calendar sync (when connected)
- **Anthropic** — AI drafting via BLU
- **Notion** — Client and notes sync (when connected)

We may also disclose information where required by law, to protect the rights, property, or safety of TAĪSTU, our clients, or others, or in connection with a business transfer such as a merger or acquisition.

---

## 7. Cookies and sessions

The TAĪSTU platform uses a single **essential session cookie** (HttpOnly and Secure) to keep you signed in and to protect your session. We do not use advertising, analytics, or cross-site tracking cookies. Because the session cookie is essential to signing in, the platform cannot function without it.

---

## 8. Data retention

We keep information for as long as we need it to provide the service and to meet our legal and record-keeping obligations. In particular:

- **Security audit logs** are retained for approximately 365 days;
- **Expired authentication tokens** are cleared after approximately 30 days;
- **Shared scope- and proposal-review links** expire approximately 30 days after they are sent;
- **Engagement records** are kept for the life of the working relationship and our business records.

An administrator can delete an engagement's data, which removes it from our active systems. Copies held in routine backups age out on their own cycle.

---

## 9. How we protect information

We take reasonable measures to protect the information in the platform, including:

- **Encryption in transit** — the platform is served over HTTPS with HSTS, uses Secure cookies, and connects to our database over encrypted connections;
- **Protected credentials** — passwords are stored only as salted hashes, with optional two-factor authentication at sign-in;
- **Access controls** — administrator permissions and per-engagement isolation so information is available only to those who should see it;
- **Abuse prevention** — rate limiting and security audit logging;
- **Upload safeguards** — file-type validation and size limits.

Our infrastructure providers offer encryption of data at rest, which we enable at the provider level. No system is perfectly secure, but we work to protect your information and to build to recognized security standards.

---

## 10. Where information is processed

TAĪSTU and its service providers are based in the United States. If you access the platform from outside the U.S., your information will be processed in the United States, which may have data-protection laws different from those in your location.

---

## 11. Your choices and rights

You may request to access, correct, or delete the personal information we hold about you, or ask a question about how it is used. Depending on where you live, you may have additional rights under laws such as the EU/UK GDPR or U.S. state privacy laws (including in Florida, where TAĪSTU is based). Invited users can also update their own account details in Settings.

To make a request, contact us at **info@taistu.com**. We will respond within the time and in the manner required by applicable law, and we may need to verify your identity before acting on a request.

---

## 12. Third-party services

The TAĪSTU platform connects to third-party services (such as Google and Notion) at your or our direction. Those services are governed by their own privacy policies, and this policy does not cover their practices.

---

## 13. Changes to this policy

We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date above, and for material changes we will take reasonable steps to let affected users know.

---

## 14. Contact us

TAĪSTU
1000 Brickell Ave, Ste 715, Miami, FL 33131
Email: **info@taistu.com**

If you have questions about this policy or how we handle your information, please reach out — we're glad to help.
`;
